import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import arrow from 'assets/arrowLeft.svg';
import Button from 'common-ui/button';
import loadIcon from 'common-ui/table/loading.svg';
import { withLeadLock } from 'components/LeadLock';
import { LEAD_STATE } from 'constants/constants';
import { getLeadById } from 'features/sales/salesActions';
import {
  selectAddNoteSuccess,
  selectAddOrderSuccess,
  selectGetLeadLoading,
  selectLeadById,
  selectNewLeadUuid,
  selectUpdateLeadB2BSuccess,
  selectUpdateLeadB2CSuccess,
  setResetAll,
  setResetLeadById,
} from 'features/sales/salesSlice';
import Success from 'pages/components/success';

import { AddNote } from './AddNote';
import { AddOrder } from './AddOrder';
import { CompetitorInfo } from './CompetitorInfo';
import { History } from './History';
import {
  AppointmentText,
  AppointmentWrapper,
  BackAction,
  BackTitle,
  Header,
  InfoCardWrapper,
  LoadContainer,
  LoadingIcon,
  NoteWrapper,
  ViewContainer,
} from './SalesLead.styles';
import { ViewOrder } from './ViewOrder';
import AddNewLead from './addNewLead/AddNewLead';
import { Card } from './components/Card';
import { LabelActions, ProductActions } from './components/LabelActions';
import { Note } from './components/Note';
import Filter from './filter';

const SalesLead = ({ leadLock, isLocked, canEdit, readOnlyMode }) => {
  const [showTooltipHistory, setShowTooltipHistory] = useState(false);
  const [showTooltipOrder, setShowTooltipOrder] = useState(false);
  const [addOrder, setAddOrder] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [showCompetitorInfo, setShowCompetitorInfo] = useState(false);
  const [addNote, setAddNote] = useState(false);
  const [showOrder, setShowOrder] = useState(false);
  const [addNewLead, setAddNewLead] = useState(false);

  const [selectedScript, setSelectedScript] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { leadId } = useParams();
  const lead = useSelector(selectLeadById);
  const successAddNote = useSelector(selectAddNoteSuccess);
  const successAddOrder = useSelector(selectAddOrderSuccess);
  const updateLeadB2BSuccess = useSelector(selectUpdateLeadB2BSuccess);
  const updateLeadB2CSuccess = useSelector(selectUpdateLeadB2CSuccess);
  const newLeadUuid = useSelector(selectNewLeadUuid);
  const [showSuccess, setShowSuccess] = useState(false);
  const getLeadLoading = useSelector(selectGetLeadLoading);

  // Use lead data from lock if available, fallback to Redux
  const leadData = leadLock?.lockedLead?.data || lead;

  const [phone, setPhone] = useState(location.state?.phone || '');
  const leadInformation = leadData?.leadInformation || {};

  const offers = leadData?.availableOffers || [];

  const handleShowHistoryTooltip = () => setShowTooltipHistory(true);
  const handleHideHistoryTooltip = () => setShowTooltipHistory(false);
  const handleShowOrderTooltip = () => setShowTooltipOrder(true);
  const handleHideOrderTooltip = () => setShowTooltipOrder(false);

  const handleEditClick = () => {
    if (!canEdit) {
      // Show a notification that lead is locked
      return;
    }
    setIsEdit(true);
    setAddNewLead(true);
  };

  const handleOpenHistory = () => setOpenHistory(true);
  const handleOpenOrder = () => setShowOrder(true);
  const handleOpenAddOrder = () => {
    if (!canEdit) return;
    setAddOrder(true);
  };
  const handleOpenCompetitorInfo = () => setShowCompetitorInfo(true);
  const handleAddNote = () => {
    if (!canEdit) return;
    setAddNote(true);
  };

  const leadInfoGroup = [
    {
      group: [
        { label: 'Company', value: leadInformation.companyName },
        {
          label: 'Full name',
          value:
            leadInformation.firstName ||
            leadInformation.lastName ||
            leadInformation?.patronymic
              ? [
                  leadInformation.firstName,
                  leadInformation.lastName,
                  leadInformation?.patronymic,
                ]
                  .filter(Boolean)
                  .join(' ')
              : null,
        },
        { label: 'Business model', value: leadInformation.leadType },
        { label: 'Email address', value: leadInformation?.email },
        { label: 'Phone', value: leadInformation.phone },
        { label: 'Address', value: leadInformation.address },
        { label: 'Description', value: leadInformation.description },
        { label: 'Lead Source', value: leadInformation.leadSource?.name },
        { label: 'Number of employees', value: leadInformation.numberOfEmployees },
        {
          label: 'State',
          value: LEAD_STATE.find((elm) => elm?.value === leadInformation.state)?.label,
        },
        { label: 'Type', value: leadInformation.type },
        { label: 'WebAddress', value: leadInformation.webAddress },
        { label: 'Added By', value: leadInformation.addedBy },
      ],
      isSelected: false,
      onClick: undefined,
    },
  ];

  const productOffersData = offers.map((elm) => ({
    group: [
      { label: 'Offer name', value: elm?.name },
      { label: 'Description', value: elm?.description },
      { label: 'Help Text', value: elm?.salesHelpText },
      { label: 'Count', value: elm?.count },
      {
        label: 'Price',
        value:
          elm?.price || elm?.currency
            ? [elm?.price, elm?.currency].filter(Boolean).join(' ')
            : null,
      },
      { label: 'Product Name', value: elm?.productName },
      { label: 'End Date', value: elm?.endDate },
      { label: 'Start Date', value: elm?.startDate },
    ],
    uuid: elm.script.uuid,
    onClick: () => handleSelectScript(elm.script),
    isSelected: selectedScript?.uuid === elm.salesScriptId,
  }));

  const scriptData = selectedScript
    ? [
        {
          group: [
            { label: 'Name', value: selectedScript?.name },
            { label: 'Description', value: selectedScript?.description },
            { label: 'Script', value: selectedScript?.script },
          ],
          isSelected: false,
          onClick: undefined,
        },
      ]
    : [];

  const competitorInfoData = [
    {
      competitor: leadInformation.competitor,
      tariff: leadInformation.tariff,
      includedServices: leadInformation.includedServices,
      contractEndDate: leadInformation.contractEndDate,
      contactDate: leadInformation.contactDate,
      nextContactDate: leadInformation.nextContactDate,
    },
  ];

  const additionalInfo =
    leadInformation.competitor ||
    leadInformation.tariff ||
    leadInformation.includedServices ||
    leadInformation.contractEndDate ||
    leadInformation.contactDate ||
    leadInformation.nextContactDate;

  const handleAppointmentNavigation = () => {
    navigate('/project-management/tickets');
  };

  const handleCloseCompetitorInfo = () => setShowCompetitorInfo(false);

  const handleAddNewLeadNavigation = () => {
    navigate('/sales/search-lead', { state: { leadId } });
  };

  const handleCloseModal = () => {
    setAddNote(false);
    setAddOrder(false);
  };

  const handleCloseAddLeadModal = () => {
    if (isEdit) {
      handleCloseAddLeadModalSuccess();
    } else {
      navigate('/sales/search-lead');
    }
  };

  const handleCloseAddLeadModalSuccess = () => {
    setIsEdit(false);
    setAddNewLead(false);
  };

  const handleSelectScript = (script) => {
    setSelectedScript(script);
  };

  const goBack = () => {
    if (location.state?.from === '/sales/search-lead') {
      navigate(-3);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (!phone) {
      // Don't fetch lead data via Redux - LeadLockGuard handles lead fetching
      // The guard either allows navigation with lead data or shows a conflict modal
      // If we're here, it means the guard succeeded and we should have lead data

      // Only fetch if we really don't have any lead data and no lock info
      // This should rarely happen since LeadLockGuard handles it
      if (!leadLock?.lockedLead?.data && !lead) {
        dispatch(getLeadById(leadId));
      }
    } else {
      setAddNewLead(true);
    }
  }, [
    dispatch,
    successAddNote,
    successAddOrder,
    updateLeadB2BSuccess,
    updateLeadB2CSuccess,
    leadId,
    leadLock?.lockedLead?.data,
    lead,
  ]);

  useEffect(() => {
    if (newLeadUuid) {
      setPhone('');
      if (leadId === 'not-found') {
        dispatch(setResetAll());
        setShowSuccess(true);
      } else {
        dispatch(setResetAll());
        dispatch(getLeadById(leadId));
      }
    }
  }, [newLeadUuid]);

  useEffect(() => {
    setSelectedScript(offers[0]?.script);
  }, [offers]);

  useEffect(() => {
    return () => {
      dispatch(setResetLeadById());
    };
  }, []);

  if (showSuccess) {
    return (
      <Success
        height="340px"
        title="New Lead Has Been Added!"
        description={`You successfully created new lead`}
        buttonText="Ok, thanks"
        route="/sales/search-lead"
      />
    );
  }
  return (
    <>
      <AddNote leadId={leadId} handleCloseModal={handleCloseModal} isModalOpen={addNote} />
      <AddOrder
        offers={offers}
        leadId={leadId}
        handleCloseModal={handleCloseModal}
        isModalOpen={addOrder}
      />
      <AddNewLead
        onClose={handleCloseAddLeadModal}
        handleClose={handleCloseAddLeadModalSuccess}
        phone={phone || leadInformation.phone}
        currentStatus={leadData?.currentStatus}
        lead={leadInformation}
        isEdit={isEdit}
        isModalOpen={addNewLead}
      />
      <ViewOrder
        data={leadData?.orders}
        onClose={() => setShowOrder(false)}
        isModalOpen={showOrder}
      />
      <CompetitorInfo
        data={competitorInfoData}
        isModalOpen={showCompetitorInfo}
        onClose={handleCloseCompetitorInfo}
      />
      <History
        onClose={() => setOpenHistory(false)}
        data={leadData?.statusHistory}
        isModalOpen={openHistory}
      />
      {!phone && (
        <ViewContainer>
          <Header>
            <BackAction onClick={goBack}>
              <img src={arrow} alt="arrow" />
              <BackTitle>Back to list</BackTitle>
            </BackAction>
            <Button onClick={handleAddNewLeadNavigation} width="90px" height="38px" secondary>
              + Add New Lead
            </Button>
          </Header>

          <Filter
            currentStatus={leadData?.currentStatus}
            leadId={leadId}
            status={leadData?.availableStatuses}
            disabled={!canEdit}
          />

          {/* Read-only mode warning */}
          {readOnlyMode && (
            <div
              style={{
                padding: '15px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '4px',
                marginBottom: '20px',
                color: '#856404',
              }}
            >
              <strong>🔒 Read-Only Mode:</strong> This lead is currently being edited by
              another user. Changes cannot be saved until it becomes available.
            </div>
          )}

          {getLeadLoading || leadLock?.isLoading?.getLead ? (
            <LoadContainer>
              <LoadingIcon src={loadIcon} alt="Loading..." />
            </LoadContainer>
          ) : (
            <InfoCardWrapper>
              <Card
                setShowCompetitorInfo={handleOpenCompetitorInfo}
                additional={additionalInfo}
                title="Lead Info"
                leadInformation={leadInformation}
                data={leadInfoGroup}
                action={
                  <LabelActions
                    lead={leadData}
                    showTooltipHistory={showTooltipHistory}
                    onShowTooltipHistory={handleShowHistoryTooltip}
                    onHideTooltipHistory={handleHideHistoryTooltip}
                    onOpenHistory={handleOpenHistory}
                    onEditClick={handleEditClick}
                    canEdit={canEdit}
                  />
                }
              />
              <Card
                title="Product offers"
                action={
                  <ProductActions
                    lead={leadData}
                    showTooltipOrder={showTooltipOrder}
                    onShowTooltipOrder={handleShowOrderTooltip}
                    onHideTooltipOrder={handleHideOrderTooltip}
                    onOpenOrder={handleOpenOrder}
                    onOpenAddOrder={handleOpenAddOrder}
                    canEdit={canEdit}
                  />
                }
                data={productOffersData}
              />
              <Card title="Script" data={scriptData} />
            </InfoCardWrapper>
          )}

          <AppointmentWrapper
            onClick={canEdit ? handleAppointmentNavigation : undefined}
            style={{ opacity: canEdit ? 1 : 0.6, cursor: canEdit ? 'pointer' : 'not-allowed' }}
          >
            <AppointmentText>+ Add Appointment</AppointmentText>
          </AppointmentWrapper>
          <AppointmentWrapper
            onClick={canEdit ? handleAddNote : undefined}
            style={{ opacity: canEdit ? 1 : 0.6, cursor: canEdit ? 'pointer' : 'not-allowed' }}
          >
            <AppointmentText $color="#FF6A00">+ Add note</AppointmentText>
          </AppointmentWrapper>

          <NoteWrapper>
            {leadData?.notes.map((elm) => (
              <Note title={elm.title} description={elm.description} key={elm.uuid} />
            ))}
          </NoteWrapper>
        </ViewContainer>
      )}
    </>
  );
};

// Wrap with lead locking HOC with custom configuration
// Note: autoLock is re-enabled to manage lock lifecycle properly
const SalesLeadWithLock = withLeadLock(SalesLead, {
  autoLock: true, // Re-enabled to manage lock lifecycle properly
  enableHeartbeat: true,
  showLockStatus: true,
  showConflictModal: false, // Disabled - LeadLockGuard handles conflicts
  showLeaveWarning: true,
  // Don't redirect on conflict - show modal instead
  redirectOnConflict: null,
  // Custom conflict handler to prevent navigation
  onLockConflict: (error) => {
    console.warn('Lead lock conflict:', error.message || error);
    // Don't navigate, let the modal handle it
  },
});

export default SalesLeadWithLock;
